import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "@/ui/toast";
import { IconButton } from "@/ui/IconButton";
import { fmtBytes } from "./quota";
import s from "./AdminTab.module.css";

interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  quotaBytes: number;
  usedBytes: number;
  noteCount: number;
  isPremium: boolean;
  isAdmin: boolean;
  createdAt: number;
  lastSeen: number | null;
}

const GB = 1024 ** 3;

export function AdminTab({ selfId }: { selfId: string }) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/users");
      if (!res.ok) throw new Error(`admin list failed (${res.status})`);
      const data = (await res.json()) as { users: AdminUser[] };
      setUsers(data.users);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const patch = async (id: string, body: Record<string, unknown>) => {
    const res = await fetch(`/api/admin/users/${id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      toast.error(data.error ?? "update failed");
    }
    await load();
  };

  const deleteUser = async (u: AdminUser) => {
    const typed = prompt(
      `Permanently delete ${u.email} and ALL their data (${u.noteCount} notes, ${fmtBytes(u.usedBytes)})?\n\nThis cannot be undone. Type the email to confirm:`,
    );
    if (typed?.trim().toLowerCase() !== u.email.toLowerCase()) {
      if (typed !== null) toast.info("Email didn't match — nothing deleted.");
      return;
    }
    const res = await fetch(`/api/admin/users/${u.id}`, { method: "DELETE" });
    if (res.ok) toast.success(`Deleted ${u.email} and all their data`);
    else {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      toast.error(data.error ?? "delete failed");
    }
    await load();
  };

  if (loading && users.length === 0) return <div className={s.meta}>Loading users…</div>;

  return (
    <div className={s.scroll}>
      <table className={s.table}>
        <thead>
          <tr>
            <th>User</th>
            <th>Storage</th>
            <th>Quota (GB)</th>
            <th>Notes</th>
            <th>Premium</th>
            <th>Admin</th>
            <th>Last seen</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const pct = u.quotaBytes > 0 ? Math.min(100, (u.usedBytes / u.quotaBytes) * 100) : 100;
            return (
              <tr key={u.id}>
                <td>
                  <span className={s.email}>
                    {u.email}
                    <span className={s.emailSub}>
                      {u.isAdmin ? <span className={[s.badge, s.badgeAdmin].join(" ")}>admin</span> : null}{" "}
                      {u.isPremium ? <span className={[s.badge, s.badgePremium].join(" ")}>premium</span> : null}
                      {!u.isAdmin && !u.isPremium ? "free" : null}
                    </span>
                  </span>
                </td>
                <td className={s.usageCell}>
                  <span className={s.usageText}>
                    {fmtBytes(u.usedBytes)} / {fmtBytes(u.quotaBytes)}
                  </span>
                  <div className={s.usageBar}>
                    <div
                      className={[s.usageFill, pct >= 100 ? s.usageOver : pct >= 85 ? s.usageWarn : ""].join(" ")}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </td>
                <td>
                  <input
                    className={s.quotaInput}
                    type="number"
                    min={0}
                    step={1}
                    defaultValue={Math.round(u.quotaBytes / GB)}
                    key={`${u.id}:${u.quotaBytes}`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                    }}
                    onBlur={(e) => {
                      const gb = Number(e.target.value);
                      if (Number.isFinite(gb) && gb >= 0 && Math.round(gb * GB) !== u.quotaBytes) {
                        void patch(u.id, { quotaBytes: Math.round(gb * GB) });
                      }
                    }}
                  />
                </td>
                <td className={s.meta}>{u.noteCount}</td>
                <td>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={u.isPremium}
                    aria-label={`Premium for ${u.email}`}
                    className={[s.toggle, u.isPremium ? s.toggleOn : ""].join(" ")}
                    onClick={() => void patch(u.id, { isPremium: !u.isPremium })}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={u.isAdmin}
                    aria-label={`Admin for ${u.email}`}
                    className={[s.toggle, u.isAdmin ? s.toggleOn : ""].join(" ")}
                    disabled={u.id === selfId}
                    style={u.id === selfId ? { opacity: 0.4 } : undefined}
                    onClick={() => {
                      if (u.id === selfId) return;
                      if (u.isAdmin || confirm(`Make ${u.email} an admin? They can see and manage every account.`)) {
                        void patch(u.id, { isAdmin: !u.isAdmin });
                      }
                    }}
                  />
                </td>
                <td className={s.meta}>{u.lastSeen ? new Date(u.lastSeen).toLocaleDateString() : "never"}</td>
                <td>
                  {u.id !== selfId ? (
                    <IconButton label={`Delete ${u.email} and all their data`} onClick={() => void deleteUser(u)}>
                      <Trash2 size={13} />
                    </IconButton>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
